import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  output,
  signal,
} from '@angular/core';
import { isValidStatsDateRange } from '../../../admin-stats-period/admin-stats-period.helpers';
import { getLastMonthRangeUtc, getLastQuarterRangeUtc } from '../../reports.helpers';
import { ReportsPeriodPreset, ReportsPeriodSelection } from '../../reports.types';

@Component({
  selector: 'admin-reports-period-filter',
  templateUrl: './reports-period-filter.html',
  host: { class: 'block shrink-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminReportsPeriodFilter implements OnInit {
  readonly periodChange = output<ReportsPeriodSelection>();

  readonly preset = signal<ReportsPeriodPreset>('last-quarter');
  readonly customStartDate = signal('');
  readonly customEndDate = signal('');

  ngOnInit(): void {
    this.selectPreset('last-quarter');
  }

  selectPreset(nextPreset: ReportsPeriodPreset): void {
    this.preset.set(nextPreset);

    if (nextPreset === 'last-quarter') {
      const quarter = getLastQuarterRangeUtc();
      this.customStartDate.set(quarter.startDate);
      this.customEndDate.set(quarter.endDate);
      this.emitSelection('last-quarter', quarter);
      return;
    }

    if (nextPreset === 'last-month') {
      const month = getLastMonthRangeUtc();
      this.customStartDate.set(month.startDate);
      this.customEndDate.set(month.endDate);
      this.emitSelection('last-month', month);
      return;
    }

    this.emitCustomIfValid();
  }

  onCustomStartInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const endDate = this.customEndDate();
    const value = endDate && input.value > endDate ? endDate : input.value;
    input.value = value;
    this.customStartDate.set(value);
    this.preset.set('custom');
    this.emitCustomIfValid();
  }

  onCustomEndInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const startDate = this.customStartDate();
    const value = startDate && input.value && input.value < startDate ? startDate : input.value;
    input.value = value;
    this.customEndDate.set(value);
    this.preset.set('custom');
    this.emitCustomIfValid();
  }

  getPresetButtonClass(nextPreset: ReportsPeriodPreset): string {
    const active = this.preset() === nextPreset;

    if (active) {
      return 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100';
    }

    return 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:bg-white/5 dark:hover:text-zinc-200';
  }

  private emitCustomIfValid(): void {
    const startDate = this.customStartDate();
    const endDate = this.customEndDate();

    if (!isValidStatsDateRange(startDate, endDate)) {
      return;
    }

    this.emitSelection('custom', { startDate, endDate });
  }

  private emitSelection(
    preset: ReportsPeriodPreset,
    range: { startDate: string; endDate: string },
  ): void {
    this.periodChange.emit({
      preset,
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }
}
